// FILE: src/services/adminUserService.js
//
// Wrappers around the admin_users RPCs introduced in migration 016.
// Caller authentication happens server-side (the RPC reads the JWT directly
// via auth.jwt() ->> 'email'), so these wrappers carry no caller-email
// argument — the client just needs to be signed in.
//
// All functions throw a plain Error with the Supabase message on failure.

import { supabase } from './supabaseClient';

/**
 * Returns the full list of admin users, ordered with bootstrap admins first.
 *
 * @returns {Promise<Array<{
 *   email: string,
 *   is_bootstrap: boolean,
 *   added_at: string,
 *   added_by: string|null,
 *   notes: string|null,
 * }>>}
 * @throws {Error} on any Supabase failure (including "not authorized").
 */
export async function listAdminUsers() {
  const { data, error } = await supabase.rpc('list_admin_users');
  if (error) throw new Error(`List admins failed: ${error.message}`);
  return Array.isArray(data) ? data : [];
}

/**
 * Adds a new admin or updates the notes on an existing one.  The caller's
 * email is captured server-side as added_by.
 *
 * @param {string} email
 * @param {string} [notes]
 * @returns {Promise<object>} the inserted/updated row
 * @throws {Error} on invalid email or any Supabase failure.
 */
export async function addAdminUser(email, notes = '') {
  if (!email || typeof email !== 'string') {
    throw new Error('addAdminUser: email is required');
  }
  const { data, error } = await supabase.rpc('add_admin_user', {
    p_email: email.trim().toLowerCase(),
    p_notes: notes ? String(notes).trim() : null,
  });
  if (error) throw new Error(`Add admin failed: ${error.message}`);
  return data;
}

/**
 * Removes an admin.  The RPC enforces:
 *   - caller must be an admin
 *   - caller cannot remove themselves
 *   - bootstrap admins cannot be removed
 *
 * @param {string} email
 * @returns {Promise<object>} the deleted row (snapshot before deletion)
 * @throws {Error} when any rule above is violated, or on any Supabase failure.
 */
export async function removeAdminUser(email) {
  if (!email || typeof email !== 'string') {
    throw new Error('removeAdminUser: email is required');
  }
  const { data, error } = await supabase.rpc('remove_admin_user', {
    p_email: email.trim().toLowerCase(),
  });
  if (error) throw new Error(`Remove admin failed: ${error.message}`);
  return data;
}
